import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { PolicyDateOptions } from '@core/interfaces/interfaces';
import { ProductType } from '@core/models/api/dsm-types';
import { CoverageModel } from '@core/models/views/coverage.model';
import { DeleteProductService } from '@core/services/delete-product.service';
import { ProductsService } from '@core/services/products.service';
import { QuoteService } from '@core/services/quote.service';
import { TaskService } from '@core/services/task.service';
import { CoverageEntity } from '@core/store/entities/coverage/coverage.entity';
import { CoveredLocationEntity } from '@core/store/entities/covered-location/covered-location.reducer';
import { PremiumEntity } from '@core/store/entities/premium/premium.entity';
import { ProductModel } from '@core/store/entities/product/product.model';
import { ErrorModel } from '@entities/error/error.model';
import { rateQuoteFail } from '@entities/quote/quote.action';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DatepickerInputComponent } from '@shared/components/datepicker-input/datepicker-input.component';
import { DateUtils } from '@shared/utils/date.utils';
import { ComponentChanges } from '@shared/utils/general.utils';
import { Nullable } from '@shared/utils/type.utils';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { PriceSummaryDisplayProduct } from '../price-summary-display/price-summary-display-content';
import { DF_PROPERTY_BLOCKED_RULE_ID } from '@shared/constants/app-constants';
import { ProductUtils } from '@shared/utils/product.util';

interface DwellingFireCoverageDisplayFormModel {
  effectiveDate: Nullable<string>;
}

@Component({
  selector: 'nwx-dwelling-fire-coverage-display',
  templateUrl: './dwelling-fire-coverage-display.component.html',
  styleUrls: ['./dwelling-fire-coverage-display.component.scss'],
  standalone: false,
})
export class DwellingFireCoverageDisplayComponent
  implements OnChanges, OnDestroy, OnInit
{
  @Input() premiums!: PremiumEntity[];
  @Input() coverages!: CoverageEntity[];
  @Input() product!: ProductModel;
  @Input() coveredLocation!: CoveredLocationEntity;
  @Input() maxSizeMini!: boolean;
  @Input() errors: ErrorModel[] = [];
  @Input() priceSummaryDisplayProduct!: PriceSummaryDisplayProduct;

  form = new FormGroup({
    effectiveDate: new FormControl('', [
      (control) =>
        DatepickerInputComponent.validate(
          control,
          this.policyDateOptions?.minDate || '',
          this.policyDateOptions?.maxDate || ''
        ),
    ]),
  });

  policyDateOptions: Nullable<PolicyDateOptions> = null;

  totalPolicyPremium = 0;
  termLengthMonths = 6;
  formattedAddress = '';
  yearRoofReplaced: number | string = 0;

  displayCoverages: CoverageModel[] = [];
  displayDeductibles: CoverageModel[] = [];
  updateInProgress$: Observable<boolean>;
  private unsubscribe$ = new Subject<void>();
  showDatePicker: boolean = false;
  formValueDate: any;
  showPremium = false;
  hasBlockedByPropertyErrorMsg = false;

  displayCoverageIds: string[] = [
    'FireBuilding',
    'FireBuildingOtherStructures',
    'FireContents',
    'FireRentalValue',
    'FireAdditionalLivingExpense',
  ];

  deductibleCoverageIds: string[] = ['SectionIDeductibles'];

  deductibleCodes: string[] = ['AllPerilDeductible'];

  constructor(
    private router: Router,
    private currencyPipe: CurrencyPipe,
    private quoteService: QuoteService,
    private modalService: NgbModal,
    private productsService: ProductsService,
    private ref: ChangeDetectorRef,
    private taskService: TaskService,
    private deleteProductService: DeleteProductService
  ) {
    this.updateInProgress$ = this.quoteService
      .getQuoteUpdateInProgress()
      .pipe(map((v) => !!v));
    this.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        withLatestFrom(this.updateInProgress$)
      )
      .subscribe(this.onFormChanged.bind(this));
  }

  ngOnDestroy(): void {
    this.form.disable();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.showDatePicker = false;
  }

  ngOnInit(): void {
    this.formatAddress();
    this.getRoofReplacementYear();
    this.productsService
      .getPolicyDateOptions(this.product?.type)
      .pipe(debounceTime(100), takeUntil(this.unsubscribe$))
      .subscribe((options) => {
        this.policyDateOptions = options;
        this.ref.detectChanges();
      });
    this.productsService
      .getSelectedProductTypes()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedProductTypes) => {
        if (
          this.product?.errorMessage?.includes(DF_PROPERTY_BLOCKED_RULE_ID) &&
          selectedProductTypes.some((p) => ProductUtils.isPropertyProduct(p))
        ) {
          this.hasBlockedByPropertyErrorMsg = true;
        }
      });
  }

  ngOnChanges(
    changes: ComponentChanges<DwellingFireCoverageDisplayComponent>
  ): void {
    if (
      changes.premiums ||
      changes.errors ||
      changes.priceSummaryDisplayProduct
    ) {
      if (
        this.errors.find(
          (e) =>
            e.productType === 'DwellingFire' &&
            e.sourceActionType === rateQuoteFail.type &&
            !e.developerMessage?.includes('EVTAlerts')
        )
      ) {
        this.showPremium = false;
      } else if (!this.priceSummaryDisplayProduct.installmentPrice) {
        this.showPremium = false;
      } else {
        this.showPremium = true;
      }
    }
    if (changes.coveredLocation) {
      this.formatAddress();
    }
    if (changes.coverages) {
      this.buildDisplayCoverages();
    }
    if (changes.product) {
      this.form.patchValue({
        effectiveDate: DateUtils.formatDsmDateToOld(
          this.product.effectiveDate || ''
        ),
      });
      this.showDatePicker = false;
    }
  }

  ngDoCheck(): void {
    if (this.formValueDate !== this.form.value.effectiveDate) {
      this.formValueDate = this.form.value.effectiveDate;
    }
  }

  private formatAddress(): void {
    const location = this.coveredLocation?.location;
    if (location) {
      const state = location.state?.toUpperCase();
      const addressLine = this.toTitleCase(location.addressLine1 || '');
      const city = this.toTitleCase(location.city || '');
      const postal = this.toTitleCase(location.postalCode || '');
      this.formattedAddress = `${addressLine}, ${city}, ${state} ${postal?.substring(
        0,
        5
      )}`;
    }
  }

  private toTitleCase(text: string): string {
    return text
      .toLocaleLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private buildDisplayCoverages(): void {
    this.displayCoverages = [];
    this.displayDeductibles = [];
    const displayEntities = this.coverages.filter(
      (c) =>
        c.productId === 'DwellingFire' &&
        this.displayCoverageIds.includes(c.coverageId)
    );
    displayEntities.forEach((cov) => {
      const val = cov.selectedValue[0]?.value || '';
      if (this.isNumber(val)) {
        this.displayCoverages.push({
          name: cov.name,
          value: this.currencyPipe.transform(val) || '',
        });
      } else if (this.isAcceptDecline(val)) {
        this.displayCoverages.push({
          name: cov.name,
          value: val === 'true' ? 'Accept' : 'Decline',
        });
      }
    });

    for (const coverage of this.coverages.filter(
      (c) =>
        c.productId === 'DwellingFire' &&
        c.available &&
        !c.selectedValue?.find(
          (v) => v.code === 'selected' && v.value === 'false'
        ) &&
        !this.displayCoverageIds.includes(c.coverageId)
    )) {
      const value =
        coverage.selectedValue?.find(
          (v) =>
            v.code === 'AllPerilDeductible' ||
            v.code === 'UnscheduledPersonalProperty'
        )?.value ||
        coverage.selectedValue?.[0]?.description ||
        'Selected';
      this.displayDeductibles.push({
        name: coverage.name,
        value,
      });
    }
  }

  isNumber(val: string | number): boolean {
    return parseInt(`${val}`, 10) ? true : false;
  }

  isAcceptDecline(val: string): boolean {
    return val === 'false' || val === 'true';
  }

  getRoofReplacementYear(): void {
    const roofRenovation = this.coveredLocation?.renovations?.find(
      (r) => r.type === 'Roof'
    );
    this.yearRoofReplaced = roofRenovation?.year || 'unknown';
  }

  editCoverages(): void {
    this.router.navigate(['/quote/coverages'], {
      queryParams: { product: 'DwellingFire' } as { product: ProductType },
    });
  }

  routeToHome(): void {
    this.router.navigate(['quote/dwelling-fire'], {});
  }

  promptToRemovePolicy(event?: Event): void {
    event?.preventDefault();
    this.deleteProductService.promptAndRemoveProduct(this.product);
  }

  promptToWithdrawPolicy(event?: Event): void {
    event?.preventDefault();
    this.deleteProductService.promptAndWithdrawProduct(this.product);
  }

  private onFormChanged(
    value: [Partial<DwellingFireCoverageDisplayFormModel>, Nullable<boolean>]
  ): void {
    const updateInprogress = value[1] === true;
    const newDate = DateUtils.formatDateToDSM(value[0]?.effectiveDate);
    if (
      updateInprogress ||
      !this.product ||
      this.product.effectiveDate === newDate ||
      !this.form.valid
    ) {
      return;
    }
    this.productsService.updateQuote({
      productType: 'DwellingFire',
      effectiveDate: newDate,
    });
  }

  scrollToTasks(): void {
    this.taskService.setShowTasks(true);
    const div = document.querySelector('.tasks-container');
    if (div) div.scrollIntoView({ behavior: 'smooth' });
  }
}
